import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiUrl } from '../api';
import './HarassmentEvidenceWorkspace.css';

const CRD_TRAINING_URL = 'https://calcivilrights.ca.gov/shpt/';
const MAX_EVIDENCE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_EVIDENCE_TYPES = new Set([
    'application/pdf',
    'image/jpeg',
    'image/png'
]);

const EMPTY_ASSIGNMENT = {
    employee_id: '',
    training_role: 'not_set',
    training_path: 'not_selected',
    course_label: '',
    training_language: '',
    employer_target_date: '',
    internal_note: ''
};

const EMPTY_EVIDENCE = {
    evidence: null,
    document_date: '',
    provider_name: ''
};

const STATUS_LABELS = {
    assignment_recorded: 'Assignment recorded',
    pending_review: 'Evidence added',
    evidence_added: 'Evidence added',
    employer_reviewed: 'Employer reviewed',
    needs_replacement: 'Needs replacement',
    archived: 'Archived'
};

const TRAINING_ROLE_LABELS = {
    not_set: 'Not set',
    supervisor: 'Supervisor',
    non_supervisor: 'Non-supervisor'
};

const TRAINING_PATH_LABELS = {
    not_selected: 'Not selected',
    official_crd: 'Official CRD training',
    other_provider: 'Other training provider'
};

const EVIDENCE_LIFECYCLE = {
    none: {
        guidance: 'No evidence has been added. Upload one file when your organization is ready to create an evidence record.',
        uploadAllowed: true,
        reviewAllowed: false,
        zipAllowed: false
    },
    pending_review: {
        guidance: 'The current evidence is awaiting an employer decision. Review it before any replacement file can be added.',
        uploadAllowed: false,
        reviewAllowed: true,
        zipAllowed: false
    },
    employer_reviewed: {
        guidance: 'Your organization recorded its review of the current evidence. This revision is final and cannot be reviewed or replaced in this workflow. CompCleared did not authenticate the document or determine legal sufficiency.',
        uploadAllowed: false,
        reviewAllowed: false,
        zipAllowed: true
    },
    needs_replacement: {
        guidance: 'Your organization marked the current evidence as needing replacement. Add a replacement file; the prior revision cannot be reviewed again.',
        uploadAllowed: true,
        reviewAllowed: false,
        zipAllowed: false
    }
};

const responseData = async (response, fallbackMessage) => {
    let data;
    try {
        data = await response.json();
    } catch (error) {
        throw new Error(fallbackMessage);
    }
    if (response.ok === false || data.success !== true) {
        throw new Error(data.error || data.message || fallbackMessage);
    }
    return data;
};

const assignmentId = (assignment) => assignment?.id ?? assignment?.assignment_id;
const evidenceId = (evidence) => evidence?.id ?? evidence?.evidence_id ?? evidence?.revision_id;

const assignmentEvidence = (assignment) => {
    if (Array.isArray(assignment.evidence)) return assignment.evidence;
    if (Array.isArray(assignment.evidence_revisions)) return assignment.evidence_revisions;
    if (assignment.current_evidence) return [assignment.current_evidence];
    return [];
};

const currentEvidence = (assignment) => {
    if (assignment.current_evidence) return assignment.current_evidence;
    const evidence = assignmentEvidence(assignment);
    return evidence.find((item) => item.is_current) || evidence[0] || null;
};

const evidenceLifecycleKey = (evidence) => {
    if (!evidence) return 'none';
    if (evidence.review_status === 'employer_reviewed') return 'employer_reviewed';
    if (evidence.review_status === 'needs_replacement') return 'needs_replacement';
    return 'pending_review';
};

const statusKey = (assignment) => {
    if (assignment.record_status === 'archived' || assignment.status === 'archived') return 'archived';
    const status = currentEvidence(assignment)?.review_status
        || assignment.workflow_status
        || assignment.review_status
        || assignment.status;
    if (status && STATUS_LABELS[status]) return status;
    return currentEvidence(assignment) ? 'evidence_added' : 'assignment_recorded';
};

const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    }).format(date);
};

const readFilename = (evidence) => (
    evidence?.original_filename || evidence?.file_name || evidence?.filename || 'Evidence file'
);

const downloadBlob = async (path, fallbackFilename, csrfToken) => {
    const response = await fetch(apiUrl(path), {
        credentials: 'include',
        headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : undefined
    });
    if (!response.ok) {
        let message = 'We could not prepare this download.';
        try {
            const data = await response.json();
            message = data.error || data.message || message;
        } catch (error) {
            // Non-JSON download errors use the cautious fallback above.
        }
        throw new Error(message);
    }
    const blob = await response.blob();
    const disposition = response.headers?.get?.('Content-Disposition') || '';
    const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const plainName = disposition.match(/filename="?([^";]+)"?/i);
    const filename = encodedName
        ? decodeURIComponent(encodedName[1])
        : plainName?.[1] || fallbackFilename;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

const HarassmentEvidenceWorkspace = () => {
    const [employees, setEmployees] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [officialResource, setOfficialResource] = useState(CRD_TRAINING_URL);
    const [uploadsEnabled, setUploadsEnabled] = useState(false);
    const [csrfToken, setCsrfToken] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [assignmentForm, setAssignmentForm] = useState(EMPTY_ASSIGNMENT);
    const [savingAssignment, setSavingAssignment] = useState(false);
    const [evidenceForms, setEvidenceForms] = useState({});
    const [reviewForms, setReviewForms] = useState({});
    const [busyAction, setBusyAction] = useState('');
    const [archiveCandidate, setArchiveCandidate] = useState(null);
    const [downloadCandidate, setDownloadCandidate] = useState(null);
    const [actionError, setActionError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [queryPreselectionApplied, setQueryPreselectionApplied] = useState(false);

    const loadWorkspace = useCallback(async ({ showLoading = true } = {}) => {
        if (showLoading) setLoading(true);
        setLoadError('');
        try {
            const [employeesResponse, assignmentsResponse, csrfResponse] = await Promise.all([
                fetch(apiUrl('/api/employees'), { credentials: 'include' }),
                fetch(apiUrl('/api/harassment-training/assignments?record_status=active'), { credentials: 'include' }),
                fetch(apiUrl('/api/csrf-token'), { credentials: 'include' })
            ]);
            const [employeesData, assignmentsData, csrfData] = await Promise.all([
                responseData(employeesResponse, 'We could not load active employees.'),
                responseData(assignmentsResponse, 'We could not load harassment-training records.'),
                responseData(csrfResponse, 'We could not prepare secure form submissions.')
            ]);
            if (!Array.isArray(employeesData.employees) || !Array.isArray(assignmentsData.assignments)) {
                throw new Error('The workspace response was not in the expected format.');
            }
            if (typeof csrfData.csrf_token !== 'string' || !csrfData.csrf_token) {
                throw new Error('We could not prepare secure form submissions.');
            }
            setEmployees(employeesData.employees.filter((employee) => (employee.status || 'active') === 'active'));
            setAssignments(assignmentsData.assignments);
            setUploadsEnabled(assignmentsData.uploads_enabled === true);
            setCsrfToken(csrfData.csrf_token);
            const resourceUrl = typeof assignmentsData.official_resource === 'string'
                ? assignmentsData.official_resource
                : assignmentsData.official_resource?.url;
            if (resourceUrl === CRD_TRAINING_URL) {
                setOfficialResource(resourceUrl);
            } else {
                setOfficialResource(CRD_TRAINING_URL);
            }
            return true;
        } catch (error) {
            setLoadError(error.message || 'We could not load the harassment-training workspace.');
            return false;
        } finally {
            if (showLoading) setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadWorkspace();
    }, [loadWorkspace]);

    const activeEmployees = useMemo(
        () => employees.filter((employee) => (employee.status || 'active') === 'active'),
        [employees]
    );

    const employeeById = useMemo(() => new Map(
        activeEmployees.map((employee) => [String(employee.id ?? employee.employee_id), employee])
    ), [activeEmployees]);

    useEffect(() => {
        if (queryPreselectionApplied || loading || activeEmployees.length === 0) return;
        const requestedEmployeeId = new URLSearchParams(window.location.search).get('employee_id');
        if (requestedEmployeeId && activeEmployees.some(
            (employee) => String(employee.id ?? employee.employee_id) === requestedEmployeeId
        )) {
            setAssignmentForm((current) => ({ ...current, employee_id: requestedEmployeeId }));
        }
        setQueryPreselectionApplied(true);
    }, [activeEmployees, loading, queryPreselectionApplied]);

    const resetMessages = () => {
        setActionError('');
        setSuccessMessage('');
    };

    const handleAssignmentChange = (event) => {
        const { name, value } = event.target;
        resetMessages();
        setAssignmentForm((current) => ({ ...current, [name]: value }));
    };

    const submitAssignment = async (event) => {
        event.preventDefault();
        resetMessages();
        setSavingAssignment(true);
        const payload = {
            employee_id: Number(assignmentForm.employee_id),
            training_role: assignmentForm.training_role,
            training_path: assignmentForm.training_path,
            course_label: assignmentForm.course_label.trim() || null,
            training_language: assignmentForm.training_language.trim() || null,
            employer_target_date: assignmentForm.employer_target_date || null,
            internal_note: assignmentForm.internal_note.trim() || null
        };
        try {
            const response = await fetch(apiUrl('/api/harassment-training/assignments'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken
                },
                credentials: 'include',
                body: JSON.stringify(payload)
            });
            await responseData(response, 'We could not record this assignment.');
            setAssignmentForm(EMPTY_ASSIGNMENT);
            setSuccessMessage('Assignment recorded. CompCleared did not determine a requirement or legal deadline.');
            await loadWorkspace({ showLoading: false });
        } catch (error) {
            setActionError(error.message || 'We could not record this assignment.');
        } finally {
            setSavingAssignment(false);
        }
    };

    const updateEvidenceForm = (id, field, value) => {
        resetMessages();
        setEvidenceForms((current) => ({
            ...current,
            [id]: { ...EMPTY_EVIDENCE, ...(current[id] || {}), [field]: value }
        }));
    };

    const submitEvidence = async (event, assignment) => {
        event.preventDefault();
        resetMessages();
        const id = assignmentId(assignment);
        const lifecycle = EVIDENCE_LIFECYCLE[evidenceLifecycleKey(currentEvidence(assignment))];
        if (!uploadsEnabled) {
            setActionError('Evidence uploads are not enabled in this environment.');
            return;
        }
        if (!lifecycle.uploadAllowed) {
            setActionError('The current evidence state does not allow another upload.');
            return;
        }
        const evidenceForm = { ...EMPTY_EVIDENCE, ...(evidenceForms[id] || {}) };
        const file = evidenceForm.evidence;
        if (!file) {
            setActionError('Choose a PDF, JPG, or PNG file before uploading evidence.');
            return;
        }
        if (!ACCEPTED_EVIDENCE_TYPES.has(file.type)) {
            setActionError('Evidence must be a PDF, JPG, or PNG file.');
            return;
        }
        if (file.size > MAX_EVIDENCE_BYTES) {
            setActionError('Evidence files must be 5 MB or smaller.');
            return;
        }
        setBusyAction(`evidence-${id}`);
        const body = new FormData();
        body.append('evidence', file);
        body.append('document_date', evidenceForm.document_date || '');
        body.append('provider_name', evidenceForm.provider_name.trim());
        try {
            const response = await fetch(apiUrl(`/api/harassment-training/assignments/${id}/evidence`), {
                method: 'POST',
                headers: { 'X-CSRF-Token': csrfToken },
                credentials: 'include',
                body
            });
            await responseData(response, 'We could not upload this evidence.');
            setEvidenceForms((current) => ({ ...current, [id]: EMPTY_EVIDENCE }));
            setSuccessMessage('Evidence added. An employer review is still a separate action.');
            await loadWorkspace({ showLoading: false });
        } catch (error) {
            setActionError(error.message || 'We could not upload this evidence.');
        } finally {
            setBusyAction('');
        }
    };

    const updateReviewForm = (id, field, value) => {
        resetMessages();
        setReviewForms((current) => ({
            ...current,
            [id]: {
                review_status: 'employer_reviewed',
                attestation: false,
                recorded_completion_date: '',
                replacement_reason: '',
                ...(current[id] || {}),
                [field]: value
            }
        }));
    };

    const submitReview = async (event, assignment) => {
        event.preventDefault();
        resetMessages();
        const id = assignmentId(assignment);
        const evidence = currentEvidence(assignment);
        const revisionId = evidenceId(evidence);
        const review = {
            review_status: 'employer_reviewed',
            attestation: false,
            recorded_completion_date: '',
            replacement_reason: '',
            ...(reviewForms[id] || {})
        };
        const lifecycle = EVIDENCE_LIFECYCLE[evidenceLifecycleKey(evidence)];
        if (!revisionId) {
            setActionError('Add evidence before recording an employer review.');
            return;
        }
        if (!lifecycle.reviewAllowed) {
            setActionError('Only current evidence awaiting employer review can receive a decision.');
            return;
        }
        if (!review.attestation) {
            setActionError('Confirm the employer-review statement before recording this review.');
            return;
        }
        if (review.review_status === 'employer_reviewed' && !review.recorded_completion_date) {
            setActionError('Enter the employer-recorded completion date before recording this review.');
            return;
        }
        if (review.review_status === 'needs_replacement' && !review.replacement_reason) {
            setActionError('Select a replacement reason for your organization’s record.');
            return;
        }
        const payload = review.review_status === 'needs_replacement'
            ? {
                review_status: 'needs_replacement',
                attestation: true,
                replacement_reason: review.replacement_reason
            }
            : {
                review_status: 'employer_reviewed',
                attestation: true,
                recorded_completion_date: review.recorded_completion_date
            };
        setBusyAction(`review-${id}`);
        try {
            const response = await fetch(
                apiUrl(`/api/harassment-training/assignments/${id}/evidence/${revisionId}/review`),
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-Token': csrfToken
                    },
                    credentials: 'include',
                    body: JSON.stringify(payload)
                }
            );
            await responseData(response, 'We could not record this employer review.');
            setReviewForms((current) => ({ ...current, [id]: undefined }));
            setSuccessMessage(
                review.review_status === 'needs_replacement'
                    ? 'Needs replacement was recorded for the current evidence.'
                    : 'Employer review recorded. CompCleared did not independently verify the evidence or training.'
            );
            await loadWorkspace({ showLoading: false });
        } catch (error) {
            setActionError(error.message || 'We could not record this employer review.');
        } finally {
            setBusyAction('');
        }
    };

    const confirmArchive = async () => {
        if (!archiveCandidate) return;
        resetMessages();
        const id = assignmentId(archiveCandidate);
        setBusyAction(`archive-${id}`);
        try {
            const response = await fetch(apiUrl(`/api/harassment-training/assignments/${id}`), {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken
                },
                credentials: 'include',
                body: JSON.stringify({ record_status: 'archived' })
            });
            await responseData(response, 'We could not archive this record.');
            setArchiveCandidate(null);
            setSuccessMessage('Record archived and removed from the active workspace.');
            await loadWorkspace({ showLoading: false });
        } catch (error) {
            setActionError(error.message || 'We could not archive this record.');
        } finally {
            setBusyAction('');
        }
    };

    const runDownload = async () => {
        if (!downloadCandidate) return;
        const { kind, assignment, evidence } = downloadCandidate;
        const id = assignment ? assignmentId(assignment) : null;
        const revisionId = evidence ? evidenceId(evidence) : null;
        setBusyAction(`download-${kind}-${id || 'all'}-${revisionId || ''}`);
        resetMessages();
        try {
            if (kind === 'csv') {
                await downloadBlob('/api/harassment-training/evidence.csv?record_status=all', 'harassment-training-record-index.csv', csrfToken);
            } else if (kind === 'zip') {
                await downloadBlob(`/api/harassment-training/assignments/${id}/evidence.zip`, `harassment-training-assignment-${id}.zip`, csrfToken);
            } else {
                await downloadBlob(
                    `/api/harassment-training/assignments/${id}/evidence/${revisionId}/download`,
                    readFilename(evidence),
                    csrfToken
                );
            }
            setDownloadCandidate(null);
            setSuccessMessage('Download prepared. Store and share personal data securely.');
        } catch (error) {
            setActionError(error.message || 'We could not prepare this download.');
        } finally {
            setBusyAction('');
        }
    };

    return (
        <main className="harassment-workspace">
            <div className="harassment-workspace__shell">
                <header className="harassment-workspace__header">
                    <p className="harassment-workspace__eyebrow">Separate evidence workspace</p>
                    <h1>Harassment-training workspace</h1>
                    <p id="harassment-workspace-purpose">
                        Record employer-created assignments, uploaded proof, and employer reviews for harassment-prevention
                        training. CompCleared does not determine whether training is required, select a course, calculate a
                        legal deadline, deliver training, or verify completion.
                    </p>
                </header>

                <aside className="harassment-workspace__resource" aria-labelledby="official-resource-heading">
                    <div>
                        <h2 id="official-resource-heading">Official California training resource</h2>
                        <p>
                            Review the California Civil Rights Department’s training page and choose what is appropriate
                            for your workplace. CRD is a separate government agency; CompCleared does not operate or
                            monitor its courses.
                        </p>
                    </div>
                    <a href={officialResource} target="_blank" rel="noreferrer">
                        Open official CRD training <span className="harassment-workspace__new-tab">(opens in a new tab)</span>
                    </a>
                </aside>

                {!loading && !loadError && !uploadsEnabled && (
                    <div className="harassment-workspace__capability" role="status">
                        Evidence uploads are not enabled in this environment. Assignments, employer review of existing
                        evidence, and permitted downloads remain available.
                    </div>
                )}

                {actionError && <div className="harassment-workspace__alert harassment-workspace__alert--error" role="alert">{actionError}</div>}
                {successMessage && <div className="harassment-workspace__alert harassment-workspace__alert--success" role="status">{successMessage}</div>}

                <section className="harassment-workspace__panel" aria-labelledby="record-assignment-heading">
                    <div className="harassment-workspace__section-heading">
                        <div>
                            <p className="harassment-workspace__kicker">Employer-entered planning record</p>
                            <h2 id="record-assignment-heading">Record an assignment</h2>
                        </div>
                    </div>
                    <p className="harassment-workspace__section-note">
                        Only active employees appear. Employee status and training role are employer-entered. CompCleared
                        does not determine which training applies.
                    </p>
                    {loading ? (
                        <div className="harassment-workspace__state" role="status">Loading workspace…</div>
                    ) : loadError ? (
                        <div className="harassment-workspace__state harassment-workspace__state--error" role="alert">
                            <p>{loadError}</p>
                            <button type="button" className="harassment-workspace__button harassment-workspace__button--secondary" onClick={() => loadWorkspace()}>
                                Try again
                            </button>
                        </div>
                    ) : activeEmployees.length === 0 ? (
                        <div className="harassment-workspace__state">
                            <h3>No active employees available</h3>
                            <p>Add an active employee in the roster before recording an assignment.</p>
                        </div>
                    ) : (
                        <form className="harassment-workspace__form" onSubmit={submitAssignment} aria-describedby="harassment-workspace-purpose assignment-target-help">
                            <div className="harassment-workspace__field">
                                <label htmlFor="harassment-employee">Employee <span aria-hidden="true">*</span></label>
                                <select id="harassment-employee" name="employee_id" value={assignmentForm.employee_id} onChange={handleAssignmentChange} required>
                                    <option value="">Select an active employee</option>
                                    {activeEmployees.map((employee) => (
                                        <option key={employee.id ?? employee.employee_id} value={employee.id ?? employee.employee_id}>
                                            {employee.full_name} — {employee.job_title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="harassment-workspace__field">
                                <label htmlFor="harassment-role">Training role (employer-entered)</label>
                                <select id="harassment-role" name="training_role" value={assignmentForm.training_role} onChange={handleAssignmentChange}>
                                    <option value="not_set">Not set</option>
                                    <option value="supervisor">Supervisor</option>
                                    <option value="non_supervisor">Non-supervisor</option>
                                </select>
                                <span className="harassment-workspace__help">CompCleared does not determine supervisory status.</span>
                            </div>
                            <div className="harassment-workspace__field">
                                <label htmlFor="harassment-path">Training path (employer-selected) <span className="harassment-workspace__optional">Optional</span></label>
                                <select id="harassment-path" name="training_path" value={assignmentForm.training_path} onChange={handleAssignmentChange}>
                                    <option value="not_selected">Not selected</option>
                                    <option value="official_crd">Official CRD training</option>
                                    <option value="other_provider">Other training provider</option>
                                </select>
                            </div>
                            <div className="harassment-workspace__field">
                                <label htmlFor="harassment-course-label">Course label <span className="harassment-workspace__optional">Optional</span></label>
                                <input id="harassment-course-label" name="course_label" type="text" value={assignmentForm.course_label} onChange={handleAssignmentChange} />
                            </div>
                            <div className="harassment-workspace__field">
                                <label htmlFor="harassment-language">Training language <span className="harassment-workspace__optional">Optional</span></label>
                                <input id="harassment-language" name="training_language" type="text" value={assignmentForm.training_language} onChange={handleAssignmentChange} />
                            </div>
                            <div className="harassment-workspace__field">
                                <label htmlFor="harassment-target-date">Employer target date <span className="harassment-workspace__optional">Optional</span></label>
                                <input id="harassment-target-date" name="employer_target_date" type="date" value={assignmentForm.employer_target_date} onChange={handleAssignmentChange} aria-describedby="assignment-target-help" />
                                <span id="assignment-target-help" className="harassment-workspace__help">For internal planning only. CompCleared does not calculate or represent a legal deadline.</span>
                            </div>
                            <div className="harassment-workspace__field harassment-workspace__field--wide">
                                <label htmlFor="harassment-internal-note">Internal note <span className="harassment-workspace__optional">Optional</span></label>
                                <textarea id="harassment-internal-note" name="internal_note" rows="3" value={assignmentForm.internal_note} onChange={handleAssignmentChange} />
                            </div>
                            <div className="harassment-workspace__form-actions">
                                <button type="submit" className="harassment-workspace__button harassment-workspace__button--primary" disabled={savingAssignment || !csrfToken}>
                                    {savingAssignment ? 'Recording assignment…' : 'Record assignment'}
                                </button>
                            </div>
                        </form>
                    )}
                </section>

                <section className="harassment-workspace__panel" aria-labelledby="active-records-heading">
                    <div className="harassment-workspace__section-heading harassment-workspace__section-heading--records">
                        <div>
                            <p className="harassment-workspace__kicker">Active records</p>
                            <h2 id="active-records-heading">Assignments and evidence</h2>
                        </div>
                        <button type="button" className="harassment-workspace__button harassment-workspace__button--secondary" onClick={() => setDownloadCandidate({ kind: 'csv' })} disabled={loading || Boolean(loadError)}>
                            Export record index (CSV)
                        </button>
                    </div>

                    {!loading && !loadError && assignments.length === 0 && (
                        <div className="harassment-workspace__state">
                            <h3>No active assignment records</h3>
                            <p>Record an employer-created assignment above when appropriate for your workplace.</p>
                        </div>
                    )}

                    {!loading && !loadError && assignments.length > 0 && (
                        <ul className="harassment-workspace__records" aria-label="Active harassment-training assignment records">
                            {assignments.map((assignment) => {
                                const id = assignmentId(assignment);
                                const employee = assignment.employee || employeeById.get(String(assignment.employee_id)) || {};
                                const evidence = currentEvidence(assignment);
                                const revisionId = evidenceId(evidence);
                                const lifecycleKey = evidenceLifecycleKey(evidence);
                                const lifecycle = EVIDENCE_LIFECYCLE[lifecycleKey];
                                const evidenceForm = { ...EMPTY_EVIDENCE, ...(evidenceForms[id] || {}) };
                                const reviewForm = {
                                    review_status: 'employer_reviewed',
                                    attestation: false,
                                    recorded_completion_date: '',
                                    replacement_reason: '',
                                    ...(reviewForms[id] || {})
                                };
                                const status = statusKey(assignment);
                                const archiveOpen = assignmentId(archiveCandidate || {}) === id;

                                return (
                                    <li className="harassment-workspace__record" key={id}>
                                        <header className="harassment-workspace__record-header">
                                            <div>
                                                <p className="harassment-workspace__record-name">{employee.full_name || assignment.employee_name_snapshot || assignment.employee_name || 'Employee record'}</p>
                                                <p>{employee.job_title || assignment.employee_job_title_snapshot || assignment.employee_job_title || 'Job title not recorded'}</p>
                                            </div>
                                            <span className={`harassment-workspace__status harassment-workspace__status--${status}`}>{STATUS_LABELS[status]}</span>
                                        </header>

                                        <dl className="harassment-workspace__metadata">
                                            <div><dt>Training role</dt><dd>{TRAINING_ROLE_LABELS[assignment.training_role] || 'Not set'}</dd></div>
                                            <div><dt>Training path</dt><dd>{TRAINING_PATH_LABELS[assignment.training_path] || 'Not selected'}</dd></div>
                                            {assignment.course_label && <div><dt>Course label</dt><dd>{assignment.course_label}</dd></div>}
                                            {assignment.training_language && <div><dt>Training language</dt><dd>{assignment.training_language}</dd></div>}
                                            {assignment.employer_target_date && <div><dt>Employer target date</dt><dd>{formatDate(assignment.employer_target_date)} <span>(internal planning)</span></dd></div>}
                                        </dl>

                                        <div className="harassment-workspace__subsection">
                                            <div className="harassment-workspace__subsection-heading">
                                                <h3>Evidence</h3>
                                                {evidence && (
                                                    <button type="button" className="harassment-workspace__text-button" onClick={() => setDownloadCandidate({ kind: 'file', assignment, evidence })}>
                                                        Download current evidence
                                                    </button>
                                                )}
                                            </div>
                                            {evidence ? (
                                                <p className="harassment-workspace__file-summary">
                                                    <strong>{readFilename(evidence)}</strong>
                                                    {evidence.provider_name && <> · {evidence.provider_name}</>}
                                                    {evidence.document_date && <> · document date {formatDate(evidence.document_date)}</>}
                                                </p>
                                            ) : <p className="harassment-workspace__muted">No evidence has been added.</p>}

                                            <div className={`harassment-workspace__notice harassment-workspace__notice--${lifecycleKey}`} role="status">
                                                {!uploadsEnabled && lifecycle.uploadAllowed
                                                    ? lifecycleKey === 'needs_replacement'
                                                        ? 'Your organization marked the current evidence as needing replacement. The prior revision cannot be reviewed again, and a replacement cannot be added in this environment.'
                                                        : 'No evidence has been added. Evidence cannot be uploaded in this environment.'
                                                    : lifecycle.guidance}
                                            </div>

                                            {uploadsEnabled && lifecycle.uploadAllowed && (
                                            <form className="harassment-workspace__compact-form" onSubmit={(event) => submitEvidence(event, assignment)}>
                                                <div className="harassment-workspace__field harassment-workspace__field--wide">
                                                    <label htmlFor={`evidence-file-${id}`}>Proof file</label>
                                                    <input
                                                        id={`evidence-file-${id}`}
                                                        type="file"
                                                        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                                                        onChange={(event) => updateEvidenceForm(id, 'evidence', event.target.files?.[0] || null)}
                                                        required
                                                    />
                                                    <span className="harassment-workspace__help">
                                                        Accepted files: PDF, JPG, or PNG, up to 5 MB. Do not upload medical information,
                                                        government identifiers, Social Security numbers, or unrelated personnel records.
                                                    </span>
                                                </div>
                                                <div className="harassment-workspace__field">
                                                    <label htmlFor={`evidence-date-${id}`}>Document date <span className="harassment-workspace__optional">Optional</span></label>
                                                    <input id={`evidence-date-${id}`} type="date" value={evidenceForm.document_date} onChange={(event) => updateEvidenceForm(id, 'document_date', event.target.value)} />
                                                </div>
                                                <div className="harassment-workspace__field">
                                                    <label htmlFor={`evidence-provider-${id}`}>Training provider <span className="harassment-workspace__optional">Optional</span></label>
                                                    <input id={`evidence-provider-${id}`} type="text" value={evidenceForm.provider_name} onChange={(event) => updateEvidenceForm(id, 'provider_name', event.target.value)} />
                                                </div>
                                                <div className="harassment-workspace__form-actions">
                                                    <button type="submit" className="harassment-workspace__button harassment-workspace__button--secondary" disabled={busyAction === `evidence-${id}`}>
                                                        {busyAction === `evidence-${id}` ? 'Uploading evidence…' : evidence ? 'Add newer evidence' : 'Add evidence'}
                                                    </button>
                                                </div>
                                            </form>
                                            )}
                                        </div>

                                        <div className="harassment-workspace__subsection">
                                            <h3>Employer review</h3>
                                            <p className="harassment-workspace__muted">
                                                This records your organization’s review of the current evidence. CompCleared does not independently verify the document or training.
                                            </p>
                                            {lifecycle.reviewAllowed ? (
                                            <form className="harassment-workspace__review-form" onSubmit={(event) => submitReview(event, assignment)}>
                                                <fieldset disabled={!revisionId || busyAction === `review-${id}`}>
                                                    <legend>Review decision</legend>
                                                    <label className="harassment-workspace__choice">
                                                        <input type="radio" name={`review-status-${id}`} value="employer_reviewed" checked={reviewForm.review_status === 'employer_reviewed'} onChange={() => updateReviewForm(id, 'review_status', 'employer_reviewed')} />
                                                        Employer reviewed
                                                    </label>
                                                    <label className="harassment-workspace__choice">
                                                        <input type="radio" name={`review-status-${id}`} value="needs_replacement" checked={reviewForm.review_status === 'needs_replacement'} onChange={() => updateReviewForm(id, 'review_status', 'needs_replacement')} />
                                                        Needs replacement
                                                    </label>

                                                    {reviewForm.review_status === 'employer_reviewed' ? (
                                                        <div className="harassment-workspace__field">
                                                            <label htmlFor={`completion-date-${id}`}>Recorded completion date <span aria-hidden="true">*</span></label>
                                                            <input id={`completion-date-${id}`} type="date" value={reviewForm.recorded_completion_date} onChange={(event) => updateReviewForm(id, 'recorded_completion_date', event.target.value)} required />
                                                        </div>
                                                    ) : (
                                                        <div className="harassment-workspace__field">
                                                            <label htmlFor={`replacement-reason-${id}`}>Replacement reason <span aria-hidden="true">*</span></label>
                                                            <select id={`replacement-reason-${id}`} value={reviewForm.replacement_reason} onChange={(event) => updateReviewForm(id, 'replacement_reason', event.target.value)} required>
                                                                <option value="">Select a reason</option>
                                                                <option value="wrong_employee">Wrong employee</option>
                                                                <option value="wrong_assignment">Wrong assignment</option>
                                                                <option value="unreadable">Unreadable document</option>
                                                                <option value="incomplete_document">Incomplete document</option>
                                                                <option value="unsupported_document">Unsupported document</option>
                                                                <option value="other">Other</option>
                                                            </select>
                                                        </div>
                                                    )}

                                                    <label className="harassment-workspace__attestation">
                                                        <input type="checkbox" checked={reviewForm.attestation} onChange={(event) => updateReviewForm(id, 'attestation', event.target.checked)} />
                                                        <span>
                                                            {reviewForm.review_status === 'needs_replacement'
                                                                ? 'I reviewed the selected evidence and am recording the replacement reason selected above. This action records my organization’s review; CompCleared does not independently verify the document or training.'
                                                                : 'I reviewed the selected evidence and believe it belongs to this employee and assignment. This action records my organization’s review; CompCleared does not independently verify the document or training.'}
                                                        </span>
                                                    </label>
                                                </fieldset>
                                                {!revisionId && <p className="harassment-workspace__help">Add evidence before recording an employer review.</p>}
                                                <button type="submit" className="harassment-workspace__button harassment-workspace__button--primary" disabled={!revisionId || !reviewForm.attestation || busyAction === `review-${id}`}>
                                                    {busyAction === `review-${id}` ? 'Recording review…' : 'Record employer review'}
                                                </button>
                                            </form>
                                            ) : (
                                                <p className="harassment-workspace__help">
                                                    {lifecycleKey === 'none'
                                                        ? 'Add evidence before recording an employer review.'
                                                        : lifecycleKey === 'employer_reviewed'
                                                            ? 'The current revision already has a final employer review and cannot be reviewed again.'
                                                            : 'The current revision was marked for replacement and cannot be reviewed again.'}
                                                </p>
                                            )}
                                        </div>

                                        <footer className="harassment-workspace__record-actions">
                                            <button type="button" className="harassment-workspace__button harassment-workspace__button--secondary" onClick={() => setDownloadCandidate({ kind: 'zip', assignment })} disabled={!lifecycle.zipAllowed}>
                                                Download evidence package
                                            </button>
                                            {!archiveOpen ? (
                                                <button type="button" className="harassment-workspace__button harassment-workspace__button--archive" onClick={() => { resetMessages(); setArchiveCandidate(assignment); }}>
                                                    Archive record
                                                </button>
                                            ) : (
                                                <div className="harassment-workspace__archive-confirmation" role="group" aria-label={`Confirm archiving ${employee.full_name || assignment.employee_name_snapshot || 'this assignment'}`}>
                                                    <p>Archive this record? It will leave the active workspace but remain stored and included in the record-index export. Archiving does not determine any legal obligation or retention requirement.</p>
                                                    <div>
                                                        <button type="button" className="harassment-workspace__button harassment-workspace__button--secondary" onClick={() => setArchiveCandidate(null)} disabled={busyAction === `archive-${id}`}>Cancel</button>
                                                        <button type="button" className="harassment-workspace__button harassment-workspace__button--danger" onClick={confirmArchive} disabled={busyAction === `archive-${id}`}>{busyAction === `archive-${id}` ? 'Archiving…' : 'Archive record'}</button>
                                                    </div>
                                                </div>
                                            )}
                                        </footer>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>

                {downloadCandidate && (
                    <section className="harassment-workspace__download-warning" role="group" aria-labelledby="download-warning-heading">
                        <h2 id="download-warning-heading">Review before downloading</h2>
                        <p>
                            This export contains only administrative records stored in CompCleared and may not contain every
                            record required for your circumstances. Employer reviewed records your organization’s review;
                            CompCleared has not authenticated the document or determined legal sufficiency. Employer target
                            dates are planning fields, not statutory deadlines. It is not a training certificate, legal
                            determination, or proof of compliance. The download contains personal data; store and share it securely.
                        </p>
                        <div>
                            <button type="button" className="harassment-workspace__button harassment-workspace__button--secondary" onClick={() => setDownloadCandidate(null)} disabled={busyAction.startsWith('download-')}>Cancel</button>
                            <button type="button" className="harassment-workspace__button harassment-workspace__button--primary" onClick={runDownload} disabled={busyAction.startsWith('download-')}>
                                {busyAction.startsWith('download-') ? 'Preparing download…' : 'Continue download'}
                            </button>
                        </div>
                    </section>
                )}
            </div>
        </main>
    );
};

export default HarassmentEvidenceWorkspace;
